import { Shield, AlertTriangle, Scale, CheckCircle, Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const ViolentCrimes = () => {
  return (
    <div className="min-h-screen">
      <Header />

      <section className="relative h-[60vh] flex items-center justify-center bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4 relative z-10 text-center">
          <Shield className="h-20 w-20 mx-auto mb-6 text-accent" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Violent Crimes Defense</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-95">
            Aggressive Defense for Serious Felony Charges
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero">
              <Link to="/appointment">Schedule Consultation</Link>
            </Button>
            <a href="tel:8329303059">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <Phone className="mr-2 h-5 w-5" />
                Call: (832) 930-3059
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">Serious Violent Crime Defense</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Violent crime charges are the most serious offenses in Texas criminal law. Convictions carry lengthy prison sentences, massive fines, and permanent felony records. If you're accused of murder, manslaughter, aggravated robbery, or any other violent crime, your freedom and future are at stake. You need an experienced trial attorney who will fight aggressively for your rights.
            </p>

            <div className="bg-destructive/10 border-l-4 border-destructive p-6 rounded-lg mb-12">
              <h3 className="text-xl font-bold mb-3 text-destructive flex items-center gap-2">
                <AlertTriangle className="h-6 w-6" />
                High Stakes - Maximum Consequences
              </h3>
              <p className="text-muted-foreground">
                Violent crimes carry the harshest penalties under Texas law, including life imprisonment or even the death penalty. These cases require immediate, experienced legal representation. Do not speak to police without an attorney present.
              </p>
            </div>

            <div className="bg-card p-8 rounded-lg shadow-lg mb-12">
              <h3 className="text-2xl font-bold mb-4">We Defend Against All Violent Crimes:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Murder",
                  "Capital Murder",
                  "Manslaughter",
                  "Aggravated Assault",
                  "Aggravated Robbery",
                  "Kidnapping",
                  "Home Invasion",
                  "Carjacking",
                  "Armed Robbery",
                  "Attempted Murder"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Defense Strategies for Violent Crimes</h3>
            <div className="space-y-6 mb-12">
              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Shield className="h-6 w-6 text-accent" />
                  Self-Defense & Defense of Others
                </h4>
                <p className="text-muted-foreground">
                  Texas law permits use of force, including deadly force, to protect yourself or others from imminent harm. We build comprehensive self-defense arguments based on the specific circumstances of your case.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Scale className="h-6 w-6 text-accent" />
                  Mistaken Identity
                </h4>
                <p className="text-muted-foreground">
                  Eyewitness identification is notoriously unreliable. We challenge witness credibility, cross-racial identifications, suggestive lineups, and memory reliability. DNA evidence and alibi witnesses can prove you weren't the perpetrator.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3">Lack of Intent</h4>
                <p className="text-muted-foreground">
                  Violent crimes require specific intent. We argue that any harm caused was accidental, the result of recklessness rather than intent, or occurred under circumstances that negate criminal intent.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3">Challenge Evidence</h4>
                <p className="text-muted-foreground">
                  We meticulously review all evidence: forensics, ballistics, DNA, fingerprints, and digital evidence. Expert witnesses can challenge the prosecution's scientific evidence and present alternative explanations.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3">Constitutional Violations</h4>
                <p className="text-muted-foreground">
                  Police must follow strict procedures. We identify violations of your rights: unlawful arrest, illegal search, failure to read Miranda rights, coerced confessions, or denial of right to counsel. Constitutional violations can lead to evidence suppression or case dismissal.
                </p>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Texas Violent Crime Penalties</h3>
            <div className="space-y-4 mb-12">
              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-2">Capital Murder</h4>
                <p className="text-muted-foreground mb-2">Murder with aggravating circumstances</p>
                <p className="text-sm text-destructive font-semibold">Penalty: Death penalty or life without parole</p>
              </div>
              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-2">First-Degree Felony Murder</h4>
                <p className="text-muted-foreground mb-2">Intentional or knowing murder</p>
                <p className="text-sm text-muted-foreground">Penalty: 5-99 years or life in prison, fine up to $10,000</p>
              </div>
              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-2">Second-Degree Felony Manslaughter</h4>
                <p className="text-muted-foreground mb-2">Recklessly causing death</p>
                <p className="text-sm text-muted-foreground">Penalty: 2-20 years prison, fine up to $10,000</p>
              </div>
              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-2">First-Degree Felony Aggravated Robbery</h4>
                <p className="text-muted-foreground mb-2">Robbery with weapon or causing serious injury</p>
                <p className="text-sm text-muted-foreground">Penalty: 5-99 years or life in prison, fine up to $10,000</p>
              </div>
            </div>

            <div className="bg-accent/10 border border-accent/20 p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-3">Why Choose Our Firm for Violent Crime Defense?</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>Experienced trial attorneys with proven success in serious felony cases</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>Thorough investigation and expert witness coordination</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>Aggressive defense from arrest through trial</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>Responsive service during business hours</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <QuickContact 
        title="Facing Serious Violent Crime Charges?"
        description="Your life is on the line. Contact us immediately for experienced trial representation."
      />

      <CTASection 
        title="Don't Face These Charges Alone"
        description="Violent crime charges require the most aggressive defense possible. Call us now."
      />
      <Footer />
    </div>
  );
};

export default ViolentCrimes;
